import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SCROLL_POSITION_PREFIX = 'maximgun:scroll-position:';

function scrollPositionKey(location) {
  return `${SCROLL_POSITION_PREFIX}${location.key}:${location.pathname}${location.search}${location.hash}`;
}

function readScrollPosition(key) {
  try {
    const value = Number(sessionStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeScrollPosition(key, value) {
  try {
    sessionStorage.setItem(key, String(Math.max(0, Math.round(value))));
  } catch {
    // Scroll restoration should never block navigation when storage is unavailable.
  }
}

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const storageKey = scrollPositionKey(location);
  const preserveScroll = navigationType !== 'POP' && location.state?.preserveScroll === true;
  const restoreKey = navigationType === 'POP' ? location.key : location.state?.restoreScrollKey;
  const activeStorageKeyRef = useRef(storageKey);
  const restorationRef = useRef(null);
  const restoringRef = useRef(false);

  // Update this during render so a scroll event caused by the next page's shorter
  // layout can never overwrite the position of the page we just left.
  activeStorageKeyRef.current = storageKey;

  if (restorationRef.current?.key !== storageKey) {
    restorationRef.current = {
      key: storageKey,
      top: restoreKey ? readScrollPosition(scrollPositionKey({ ...location, key: restoreKey })) : 0,
    };
  }

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previousMode = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previousMode; };
  }, []);

  useEffect(() => {
    let frameId;
    const pendingPositions = new Map();

    const flushPositions = () => {
      frameId = undefined;
      pendingPositions.forEach((top, key) => writeScrollPosition(key, top));
      pendingPositions.clear();
    };

    const rememberPosition = () => {
      if (restoringRef.current) return;
      pendingPositions.set(activeStorageKeyRef.current, window.scrollY);
      if (frameId === undefined) frameId = window.requestAnimationFrame(flushPositions);
    };

    const rememberPositionImmediately = () => {
      if (!restoringRef.current) {
        writeScrollPosition(activeStorageKeyRef.current, window.scrollY);
      }
    };

    window.addEventListener('scroll', rememberPosition, { passive: true });
    window.addEventListener('pagehide', rememberPositionImmediately);

    return () => {
      window.removeEventListener('scroll', rememberPosition);
      window.removeEventListener('pagehide', rememberPositionImmediately);
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
      flushPositions();
    };
  }, []);

  useLayoutEffect(() => {
    // Inline catalogue settings keep the viewport still. Save the new entry
    // even when no scroll event fires, so Back can restore it later.
    if (preserveScroll) {
      restoringRef.current = false;
      writeScrollPosition(storageKey, window.scrollY);
      return undefined;
    }

    let stopped = false;
    let retryTimer;
    const targetTop = restorationRef.current?.top || 0;
    const keepRestoring = targetTop > 0;
    const deadline = Date.now() + 10000;

    restoringRef.current = keepRestoring;

    const stopRestoring = () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      restoringRef.current = false;
    };

    const restore = () => {
      if (stopped) return;
      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body?.scrollHeight || 0,
      );
      const maxTop = Math.max(0, documentHeight - window.innerHeight);
      window.scrollTo({ top: Math.min(targetTop, maxTop), left: 0 });

      // Search results arrive asynchronously. Keep correcting the position until
      // the list has regained its full height, unless the visitor starts scrolling.
      if (keepRestoring && Date.now() < deadline) {
        retryTimer = window.setTimeout(restore, 100);
      }
    };

    const userStartedScrolling = () => stopRestoring();
    const interruptionEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    if (keepRestoring) {
      interruptionEvents.forEach((eventName) => window.addEventListener(eventName, userStartedScrolling, { passive: true }));
    }

    restore();
    if (!keepRestoring) restoringRef.current = false;

    return () => {
      stopRestoring();
      interruptionEvents.forEach((eventName) => window.removeEventListener(eventName, userStartedScrolling));
    };
  }, [navigationType, preserveScroll, storageKey]);

  return null;
}
